// Overhead's data layer: ONE place, and what the sky is doing over it.
//
// store.js is generated from the plan and gives every entity the same
// list-with-CRUD shape. Overhead does not have lists. It has one location, one
// observation, twelve forecast hours and at most one warning in force, so
// everything here narrows that store to those — a page asks for `place` rather
// than remembering it is `locations.value[0]`.
//
// THE DEMO ROWS ARE OURS, NOT THE STORE'S. The generated seeds are the
// factory's generic three-of-everything ("Location 1", chance: 1), which draws
// a three-point flat trace and tells nobody anything. Rows with a negative id
// belong to that seed and are filtered out below; `demoData` here seeds
// weather instead, so /demo and the QA `populated` fixture show a real band
// passing over, and the `empty` fixture still renders an empty pluviograph.
//
// WHERE THE NUMBERS COME FROM. No forecast provider is wired up, and
// pretending otherwise would be worse than saying so. `sampleFeed()`
// synthesises a plausible twelve hours from the coordinates and the clock, and
// FEED_SOURCE is 'sample' so every screen can admit it in words. Swap
// sampleFeed for a real fetch and nothing above it changes: the shapes below
// are the plan's, field for field.

import { computed, ref } from 'vue';
import { demoData } from './core/demo.js';
import {
  locations, addLocation, saveLocation,
  observations, addObservation, removeObservation,
  forecastHours, addForecastHour, removeForecastHour,
  forecastDays, addForecastDay, removeForecastDay,
  weatherWarnings, addWeatherWarning, removeWeatherWarning,
  storeReady,
} from './store.js';
import { currentPosition } from '../addons/maps/index.js';

/** Say it out loud everywhere it is shown. See the header. */
export const FEED_SOURCE = 'sample';

const HOUR = 3_600_000;
const SLOTS = 12;
const COMPASS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];

/** Bearing the wind is coming FROM, in degrees, for the plan view on Radar. */
export const bearingOf = (dir) => COMPASS.indexOf(String(dir || 'n').toLowerCase()) * 45;

// A minute is the finest granularity anything here cares about — staleness,
// which hour is "now", whether a warning has expired. Recomputing those from a
// fresh `new Date()` inside a computed would never invalidate.
const tick = ref(Date.now());
if (typeof window !== 'undefined') setInterval(() => { tick.value = Date.now(); }, 60_000);
export const nowMs = computed(() => tick.value);
export const nowIso = computed(() => new Date(tick.value).toISOString());

const topOfHour = (ms) => Math.floor(ms / HOUR) * HOUR;
const round1 = (n) => Math.round(n * 10) / 10;

// Deterministic noise. The same place at the same hour must give the same
// shape: a forecast that reshuffles on every render is not a forecast.
function rand(seed) {
  let s = (seed >>> 0) || 1;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/**
 * A plausible twelve hours over a point. Sample data, clearly labelled at every
 * call site — see FEED_SOURCE.
 *
 * @param {{lat?:number, lng?:number, at?:number, force?:'wet'|'dry'}} opts
 */
export function sampleFeed({ lat = -37.79, lng = 144.98, at = Date.now(), force = null } = {}) {
  const start = topOfHour(at);
  const next = rand(
    Math.round((lat + 90) * 997) ^ Math.round((lng + 180) * 1009) ^ Math.floor(start / HOUR),
  );

  const wet = force ? force === 'wet' : next() < 0.6;
  const centre = 3 + next() * 6;          // which hour the band peaks over
  const width = 1.5 + next() * 2;         // how long it takes to pass
  const peakMm = wet ? 1.2 + next() * 6 : 0;
  const dir = COMPASS[Math.floor(next() * COMPASS.length)];

  const hours = Array.from({ length: SLOTS }, (_, i) => {
    const bell = Math.exp(-(((i - centre) ** 2) / (2 * width * width)));
    const drizzle = next();
    return {
      time: new Date(start + i * HOUR).toISOString(),
      chance: wet
        ? Math.min(95, Math.round(6 + 84 * bell))
        : Math.round(2 + 10 * drizzle),
      mm: wet ? round1(peakMm * bell) : 0,
    };
  });

  // Temperature: a latitude-and-season base with a diurnal swing on top. Enough
  // to look like weather; not a claim about the weather.
  const month = new Date(start).getMonth();
  const seasonal = Math.cos(((month - 0) / 12) * 2 * Math.PI) * (lat < 0 ? 7 : -7);
  const base = 21 - Math.abs(lat) * 0.22 + seasonal;
  const hourOfDay = new Date(start).getHours();
  const diurnal = -Math.cos(((hourOfDay - 3) / 24) * 2 * Math.PI) * 4.5;
  const windKph = Math.round(6 + next() * 32);
  const tempC = round1(base + diurnal - (wet ? 1.5 : 0));

  const observation = {
    recordedAt: new Date(start + Math.min(58, new Date(at).getMinutes()) * 60_000).toISOString(),
    tempC,
    feelsLikeC: round1(tempC - windKph * 0.06 - (wet ? 0.8 : 0)),
    windKph,
    windDirection: dir,
    rainSince9amMm: round1(wet ? next() * 3 : 0),
  };

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start + i * 24 * HOUR);
    const chance = Math.round(next() * 95);
    const mmLow = chance > 30 ? round1(next() * 2) : 0;
    return {
      date: d.toISOString().slice(0, 10),
      minC: round1(base - 5 + next() * 3),
      maxC: round1(base + 4 + next() * 4),
      chance,
      mmLow,
      mmHigh: round1(mmLow + (chance > 30 ? 1 + next() * 9 : 0)),
      summary: chance > 60 ? 'Showers' : chance > 30 ? 'Possible shower' : 'Mostly clear',
    };
  });

  // A warning only when the band is worth one. Most days do not have one, and a
  // permanent red strip is a strip nobody reads.
  const warning = peakMm >= 5
    ? {
      headline: 'Severe thunderstorm warning',
      severity: peakMm >= 6.5 ? 'severe' : 'moderate',
      body: 'Severe thunderstorms are possible, with heavy rainfall that may lead to '
        + 'flash flooding. This is sample text standing in for the bureau bulletin '
        + 'a real feed would carry.',
      issuedAt: new Date(start).toISOString(),
      expiresAt: new Date(start + Math.round(centre + 4) * HOUR).toISOString(),
    }
    : null;

  return { observation, hours, days, warning };
}

// ---------------------------------------------------------------------------
// Demo rows. See the header: these replace the generated seed, they do not add
// to it. Forced wet, because the whole point of the trace is what a band looks
// like, and a demo that rolls a dry day shows none of it.

const DEMO = { lat: -37.786, lng: 144.978, name: 'Fitzroy North', postcode: '3068' };
const demoFeed = sampleFeed({ lat: DEMO.lat, lng: DEMO.lng, force: 'wet' });

// The warning is FORCED, not left to the peak-millimetre rule that governs a
// real one. The plan gives warning-detail a `representative` URL, QA opens it
// on every run, and a demo day that happens to roll 4 mm would leave that
// screen with nothing to show through no fault of the screen.
const demoWarningFeed = demoFeed.warning ?? {
  headline: 'Severe thunderstorm warning',
  severity: 'severe',
  body: 'Severe thunderstorms are possible, with heavy rainfall that may lead to '
    + 'flash flooding. This is sample text standing in for the bureau bulletin '
    + 'a real feed would carry.',
  issuedAt: new Date().toISOString(),
  expiresAt: new Date(Date.now() + 6 * HOUR).toISOString(),
};

const demoLocation = demoData([{
  id: -101, entity: 'location',
  name: DEMO.name, postcode: DEMO.postcode,
  latitude: DEMO.lat, longitude: DEMO.lng,
  detectedAt: new Date().toISOString(),
  rainThresholdMm: 0.2, warningsEnabled: true,
  permissionState: 'granted', pushToken: '', pushTokenUpdatedAt: null,
}]);
const demoObservations = demoData([{ id: -102, entity: 'observation', location: -101, ...demoFeed.observation }]);
const demoHours = demoData(demoFeed.hours.map((h, i) => ({ id: -200 - i, entity: 'forecast-hour', location: -101, ...h })));
const demoDays = demoData(demoFeed.days.map((d, i) => ({ id: -300 - i, entity: 'forecast-day', location: -101, ...d })));

// THE ID IS THE ONE THE PLAN DECLARES. warning-detail's `representative` URL is
// /warnings/wrn-8821, and QA opens exactly that. With a numeric demo id it
// resolved to nothing and QA photographed "This warning has expired or been
// withdrawn" as though that were the screen — which is the failure the
// `representative` field exists to prevent, arriving from the other side.
//
// A string id is deliberate and safe: `real()` filters on Number(id) > 0, and
// Number('wrn-8821') is NaN, so this can never be mistaken for a stored row.
const demoWarnings = demoData([{
  id: 'wrn-8821', entity: 'weather-warning', location: -101, ...demoWarningFeed,
}]);

// ---------------------------------------------------------------------------
// The one place.

const real = (rows) => rows.filter((r) => Number(r.id) > 0);

/** The single stored location row, whatever state it is in — including denied. */
export const locationRecord = computed(
  () => real(locations.value)[0] ?? demoLocation.value[0] ?? null,
);

/** The place, only once it actually IS a place. Denied and unasked give null. */
export const place = computed(() => {
  const row = locationRecord.value;
  return row && row.name ? row : null;
});

/** granted | denied | unasked — three states, not two. */
export const permissionState = computed(
  () => locationRecord.value?.permissionState || 'unasked',
);

export const thresholdMm = computed(() => {
  const v = Number(locationRecord.value?.rainThresholdMm);
  return Number.isFinite(v) && v > 0 ? v : 0.2;
});

export const warningsEnabled = computed(() => !!locationRecord.value?.warningsEnabled);

// Rows belonging to the current place. A demo place cannot own stored rows, so
// it reads its own seed instead — which is also what keeps the two from mixing.
function owned(rows, demo) {
  const row = locationRecord.value;
  if (!row) return [];
  if (Number(row.id) < 0) return demo;
  return real(rows).filter((r) => String(r.location) === String(row.id));
}

export const observation = computed(() => {
  const rows = owned(observations.value, demoObservations.value);
  return rows.slice().sort(
    (a, b) => new Date(b.recordedAt) - new Date(a.recordedAt),
  )[0] ?? null;
});

/**
 * The twelve hours the trace draws.
 *
 * Hours already past are dropped rather than redrawn: the now-rule sits at the
 * left edge by construction, so a stale leading hour would put "now" an hour
 * ago and quietly lie. No interpolation to fill the gap that leaves — a short
 * trace is the honest answer, and RainTrace draws one.
 */
export const traceHours = computed(() => {
  const floor = topOfHour(tick.value);
  return owned(forecastHours.value, demoHours.value)
    .filter((h) => new Date(h.time).getTime() >= floor)
    .slice()
    .sort((a, b) => new Date(a.time) - new Date(b.time))
    .slice(0, SLOTS);
});

export const weekDays = computed(() => owned(forecastDays.value, demoDays.value)
  .slice()
  .sort((a, b) => String(a.date).localeCompare(String(b.date)))
  .slice(0, 7));

export const activeWarning = computed(() => owned(weatherWarnings.value, demoWarnings.value)
  .find((w) => !w.expiresAt || new Date(w.expiresAt).getTime() > tick.value) ?? null);

/** Warning by id, demo rows included — the detail screen is deep-linkable. */
export function warningById(id) {
  const all = [...real(weatherWarnings.value), ...demoWarnings.value];
  return all.find((w) => String(w.id) === String(id)) ?? null;
}

export const warningExpired = (w) => !!w?.expiresAt && new Date(w.expiresAt).getTime() <= tick.value;

// ---------------------------------------------------------------------------
// Writing.

async function ensureRow() {
  await storeReady;
  const existing = real(locations.value)[0];
  if (existing) return existing;
  return addLocation({
    name: '', postcode: '', latitude: 0, longitude: 0,
    rainThresholdMm: 0.2, warningsEnabled: false, permissionState: 'unasked',
  });
}

function patch(row, changes) {
  if (!row) return null;
  if (Number(row.id) < 0) return Object.assign(row, changes);   // demo row: live, not stored
  return saveLocation({ ...row, ...changes });
}

function clearFeed(locationId) {
  for (const r of real(observations.value)) {
    if (String(r.location) === String(locationId)) removeObservation(r.id);
  }
  for (const r of real(forecastHours.value)) {
    if (String(r.location) === String(locationId)) removeForecastHour(r.id);
  }
  for (const r of real(forecastDays.value)) {
    if (String(r.location) === String(locationId)) removeForecastDay(r.id);
  }
  for (const r of real(weatherWarnings.value)) {
    if (String(r.location) === String(locationId)) removeWeatherWarning(r.id);
  }
}

function writeFeed(locationId, feed) {
  clearFeed(locationId);
  addObservation({ location: locationId, ...feed.observation });
  for (const h of feed.hours) addForecastHour({ location: locationId, ...h });
  for (const d of feed.days) addForecastDay({ location: locationId, ...d });
  if (feed.warning) addWeatherWarning({ location: locationId, ...feed.warning });
}

/**
 * Ask for location, and draw whatever the answer allows.
 *
 * Called from a control the person pressed, never from boot: a location prompt
 * before they have seen anything is the fastest way to be denied forever. A
 * refusal is an answer — it is recorded as `denied` and the paper stays.
 */
export async function useMyLocation() {
  const row = await ensureRow();
  const pos = await currentPosition({ highAccuracy: false });
  if (!pos) {
    patch(row, { permissionState: 'denied' });
    return null;
  }
  const lat = Math.round(pos.lat * 10_000) / 10_000;
  const lng = Math.round(pos.lng * 10_000) / 10_000;
  const saved = patch(row, {
    // No reverse geocoder is wired up, so the app does not invent a suburb it
    // cannot know. The coordinates go where the postcode would.
    name: 'Your location',
    postcode: `${lat.toFixed(3)}, ${lng.toFixed(3)}`,
    latitude: lat,
    longitude: lng,
    detectedAt: new Date().toISOString(),
    permissionState: 'granted',
  });
  writeFeed(saved.id, sampleFeed({ lat, lng }));
  return saved;
}

/** Re-read for a place already granted. No prompt: the answer is already given. */
export async function refresh() {
  const row = locationRecord.value;
  if (!row || row.permissionState !== 'granted') return null;
  if (Number(row.id) < 0) return row;                   // demo rows refresh to themselves
  const pos = await currentPosition({ highAccuracy: false });
  const lat = pos ? Math.round(pos.lat * 10_000) / 10_000 : row.latitude;
  const lng = pos ? Math.round(pos.lng * 10_000) / 10_000 : row.longitude;
  writeFeed(row.id, sampleFeed({ lat, lng }));
  return patch(row, { latitude: lat, longitude: lng, detectedAt: new Date().toISOString() });
}

export const setThreshold = (mm) => patch(locationRecord.value, { rainThresholdMm: Number(mm) });
export const setWarningsEnabled = (on) => patch(locationRecord.value, { warningsEnabled: !!on });
export const setPushToken = (token) => patch(locationRecord.value, {
  pushToken: token || '', pushTokenUpdatedAt: new Date().toISOString(),
});

// ---------------------------------------------------------------------------
// Words for numbers. Anything carried by colour is also carried by text, so
// these are used by aria-labels as well as by the visible summary.

export const clock = (iso) => (iso
  ? new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false })
  : '');

export const clockShort = (iso) => (iso
  ? new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', hour12: true }).replace(' ', '').toLowerCase()
  : '');

/** The one-line forecast under the trace. */
export const forecastSummary = computed(() => {
  const hours = traceHours.value;
  if (!hours.length) return 'No forecast for this place yet.';
  const wet = hours.filter((h) => (h.mm ?? 0) >= thresholdMm.value);
  if (!wet.length) return `No rain in the next ${hours.length} hours.`;
  const peak = wet.reduce((a, b) => ((b.mm ?? 0) > (a.mm ?? 0) ? b : a));
  const easing = wet[wet.length - 1] !== hours[hours.length - 1];
  return `Rain likely from ${clockShort(wet[0].time)}, heaviest around `
    + `${clockShort(peak.time)}${easing ? ', easing after' : ''}.`;
});
